import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  isStaticTaskSchemaNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {getFieldPairKey} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

/**
 * Get the transition for a source/target node pair
 */
function getTransitionForNodeIdPair(
  sourceNodeId: string,
  targetNodeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): {transition: Transition; sourcePTDVId: string; targetPTDVId: string} | undefined {
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  const targetNode = nodes.find((n) => n.id === targetNodeId);

  if (!sourceNode || !targetNode) {
    return undefined;
  }
  if (!isStaticTaskSchemaNodeData(sourceNode) || !isStaticTaskSchemaNodeData(targetNode)) {
    return undefined;
  }

  const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
  const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
  const transition = Object.values(transitions).find(
    (t) =>
      t.targetPTDVId === targetPTDVId &&
      Array.isArray(t.sourcePTDVIds) &&
      t.sourcePTDVIds.includes(sourcePTDVId)
  );

  return transition != null ? {transition, sourcePTDVId, targetPTDVId} : undefined;
}

/**
 * Get custom mapping data for a data flow edge from the transitions structure.
 */
export function getCustomMappingDataForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): CustomMappingData | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const pair = getTransitionForNodeIdPair(edge.source, edge.target, transitions, nodes);
  if (!pair) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId: pair.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: pair.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  return pair.transition.customMappingDataByFieldPair[fieldPairKey] ?? null;
}

/**
 * Get custom mapping data for a data flow edge by ID.
 */
export function getCustomMappingDataForEdge(
  edgeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>
): CustomMappingData | null {
  const edge = edges.find((e) => e.id === edgeId);
  if (edge == null || edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }
  return getCustomMappingDataForDataFlowEdge(edge, transitions, nodes);
}

/**
 * Get transition source for a node pair from the transitions structure.
 */
export function getTransitionSourceForNodeIdPair(
  sourceNodeId: string,
  targetNodeId: string,
  transitions: Record<string, Transition>,
  nodes: Array<StaticFlowchartNodeData>
): TransitionSource | undefined {
  const pair = getTransitionForNodeIdPair(sourceNodeId, targetNodeId, transitions, nodes);
  return pair?.transition.transitionSourcesBySourcePTDVId[pair.sourcePTDVId];
}
